import { useEffect, useState } from 'react';
import {
	Card,
	Divider,
	Pagination,
	Button,
	Modal,
	Form,
	Input,
	message,
	Select,
} from 'antd';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../methods/redux/hooks';
import remakeQueryString from '../../methods/utils/remake-query-string';
import { getWalletSnapshot } from '../../methods/redux/reducers/reports';
import Snapshot from './snapshot';
import baseuri from '../../methods/baseuri';
import { getCurrencies } from '../../methods/redux/reducers/currency';

const WalletSnapshot = () => {
	const dispatch = useAppDispatch();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [form] = Form.useForm();

	const { walletSnapshotReport } = useAppSelector((store) => store.reports);
	const { currencies } = useAppSelector((store) => store.currencies);

	const [isLoadingCreateSnapshot, setIsLoadingCreateSnapshot] = useState(false);

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());
		if (Object.keys(queryData).length === 0) {
			setSearchParams({
				pageId: '1',
				perPage: String(walletSnapshotReport.paginator.perPage),
			});
		} else {
			dispatch(getWalletSnapshot(queryData));
		}

		dispatch(getCurrencies({}));
	}, [searchParams, dispatch, walletSnapshotReport.paginator.perPage]);

	const updateQueryString = (query: any, action = 'update') => {
		remakeQueryString(query, searchParams, setSearchParams, action);
	};

	const currencySelect = currencies.map((item: any) => ({
		label: item.symbol,
		value: item._id,
	}));

	const showModal = () => {
		setIsModalVisible(true);
	};

	const handleCancel = () => {
		setIsModalVisible(false);
		form.resetFields();
	};

	const handleSubmit = async (values: any) => {
		setIsLoadingCreateSnapshot(true);
		try {
			const payload = {
				currencyId: values.currencyId,
				title: values.title,
			};

			const res: any = await baseuri.post(`admin/wallet-snap-shot`, {
				...payload,
			});

			if (res.status === 200) {
				message.success('Snapshot created successfully!');
				setIsModalVisible(false);
				form.resetFields();
			} else {
				message.info(res.data.message);
			}

			// Refresh the snapshot list
			dispatch(getWalletSnapshot(Object.fromEntries(searchParams.entries())));
		} catch (error) {
			message.error('Failed to create snapshot. Please try again.');
		} finally {
			setIsLoadingCreateSnapshot(false);
		}
	};

	return (
		<Card>
			<h1>Wallet Snapshot</h1>
			<p>List of wallet balances for various currencies.</p>

			<Button
				type="primary"
				onClick={showModal}
				style={{ marginTop: '16px', marginBottom: '16px' }}
			>
				Create Snapshot
			</Button>

			<Divider />

			<p>
				Total of{' '}
				<strong>{walletSnapshotReport.paginator.itemCount} items</strong> found
			</p>

			<Divider />

			<Pagination
				onChange={(pageId) => updateQueryString({ pageId })}
				total={walletSnapshotReport.paginator.itemCount}
				current={walletSnapshotReport.paginator.currentPage}
				pageSize={walletSnapshotReport.paginator.perPage}
				showSizeChanger
				showQuickJumper
			/>

			<br />

			{walletSnapshotReport.itemsList.map((item: any) => (
				<Snapshot key={item._id} item={item} />
			))}

			<br />

			<Divider />
			<Pagination
				onChange={(pageId) => updateQueryString({ pageId })}
				total={walletSnapshotReport.paginator.itemCount}
				current={walletSnapshotReport.paginator.currentPage}
				pageSize={walletSnapshotReport.paginator.perPage}
				showSizeChanger
				showQuickJumper
			/>

			{/* Modal for Creating Snapshot */}
			<Modal
				title="Create Snapshot"
				visible={isModalVisible}
				onCancel={handleCancel}
				footer={null}
			>
				<Form form={form} onFinish={handleSubmit}>
					<Form.Item
						name="currencyId"
						label="Currency"
						rules={[{ required: true, message: 'Please select a currency!' }]}
					>
						<Select placeholder="Select a currency" options={currencySelect} />
					</Form.Item>

					<Form.Item
						name="title"
						label="Title"
						rules={[{ required: true, message: 'Please input the title!' }]}
					>
						<Input placeholder="Enter Title" />
					</Form.Item>

					<Form.Item>
						<Button
							type="primary"
							htmlType="submit"
							disabled={isLoadingCreateSnapshot}
						>
							{isLoadingCreateSnapshot ? 'Creating...' : 'Create'}
						</Button>
						<Button onClick={handleCancel} style={{ marginLeft: '8px' }}>
							Cancel
						</Button>
					</Form.Item>
				</Form>
			</Modal>
		</Card>
	);
};

export default WalletSnapshot;
